import type CameraControls from "camera-controls";
import { HOLD_EVENT_TYPE, KeyboardKeyHold } from "hold-event";
import type { DispatcherEvent } from "hold-event/dist/EventDispatcher";
import { MathUtils } from "three";

export const CAMERA_SPEED_DEFAULTS = {
	leftTruck: 0.01,
	rightTruck: 0.01,
	upTruck: 0.01,
	downTruck: 0.01,
	rotateLeft: 0.1,
	rotateRight: 0.1,
	rotateUp: 0.05,
	rotateDown: 0.05,
	dollyUp: 0.01,
	dollyDown: 0.01,
	zoomIn: 0.01,
	zoomOut: 0.01,
};

export class KeyboardControl {
	cameraControls: CameraControls;
	#keys: Record<string, KeyboardKeyHold> = {};
	keyDirectionMapping: Record<string, "left" | "right" | "up" | "down"> = {
		ArrowLeft: "left",
		ArrowRight: "right",
		ArrowUp: "up",
		ArrowDown: "down",
	};
	arrowKeyPressHandler = this.#handleArrowKeys.bind(this);
	plusAndMinusKeyHandler = this.#handlePlusAndMinusKeys.bind(this);
	constructor({ cameraControls }: { cameraControls: CameraControls }) {
		this.cameraControls = cameraControls;

		// Initialize keys
		this.#keys.ArrowLeft = new KeyboardKeyHold("ArrowLeft", 100);
		this.#keys.ArrowRight = new KeyboardKeyHold("ArrowRight", 100);
		this.#keys.ArrowUp = new KeyboardKeyHold("ArrowUp", 100);
		this.#keys.ArrowDown = new KeyboardKeyHold("ArrowDown", 100);
		this.#keys.Equal = new KeyboardKeyHold("Equal", 100);
		this.#keys.Minus = new KeyboardKeyHold("Minus", 100);
	}

	isMouseControlEnabled(actionCode: number) {
		if (
			this.cameraControls.mouseButtons.left === actionCode ||
			this.cameraControls.mouseButtons.middle === actionCode ||
			this.cameraControls.mouseButtons.right === actionCode ||
			this.cameraControls.mouseButtons.wheel === actionCode
		) {
			return true;
		}

		return false;
	}

	initialize() {
		this.#keys.ArrowLeft.addEventListener(
			HOLD_EVENT_TYPE.HOLDING,
			this.arrowKeyPressHandler,
		);
		this.#keys.ArrowRight.addEventListener(
			HOLD_EVENT_TYPE.HOLDING,
			this.arrowKeyPressHandler,
		);
		this.#keys.ArrowUp.addEventListener(
			HOLD_EVENT_TYPE.HOLDING,
			this.arrowKeyPressHandler,
		);
		this.#keys.ArrowDown.addEventListener(
			HOLD_EVENT_TYPE.HOLDING,
			this.arrowKeyPressHandler,
		);
		this.#keys.Equal.addEventListener(
			HOLD_EVENT_TYPE.HOLDING,
			this.plusAndMinusKeyHandler,
		);
		this.#keys.Minus.addEventListener(
			HOLD_EVENT_TYPE.HOLDING,
			this.plusAndMinusKeyHandler,
		);
	}

	dispose() {
		this.#keys.ArrowLeft.removeEventListener(
			HOLD_EVENT_TYPE.HOLDING,
			this.arrowKeyPressHandler,
		);
		this.#keys.ArrowRight.removeEventListener(
			HOLD_EVENT_TYPE.HOLDING,
			this.arrowKeyPressHandler,
		);
		this.#keys.ArrowUp.removeEventListener(
			HOLD_EVENT_TYPE.HOLDING,
			this.arrowKeyPressHandler,
		);
		this.#keys.ArrowDown.removeEventListener(
			HOLD_EVENT_TYPE.HOLDING,
			this.arrowKeyPressHandler,
		);
		this.#keys.Equal.removeEventListener(
			HOLD_EVENT_TYPE.HOLDING,
			this.plusAndMinusKeyHandler,
		);
		this.#keys.Minus.removeEventListener(
			HOLD_EVENT_TYPE.HOLDING,
			this.plusAndMinusKeyHandler,
		);
	}
	#handleCameraRotation(
		direction: "left" | "right" | "up" | "down",
		event?: DispatcherEvent,
	) {
		if (!this.isMouseControlEnabled(1)) {
			return;
		}
		switch (direction) {
			case "up":
				return this.cameraControls.rotate(
					0,
					-CAMERA_SPEED_DEFAULTS.rotateUp *
						MathUtils.DEG2RAD *
						event?.deltaTime,
					true,
				);
			case "down":
				return this.cameraControls.rotate(
					0,
					CAMERA_SPEED_DEFAULTS.rotateDown *
						MathUtils.DEG2RAD *
						event?.deltaTime,
					true,
				);
			case "left":
				return this.cameraControls.rotate(
					-CAMERA_SPEED_DEFAULTS.rotateLeft *
						MathUtils.DEG2RAD *
						event?.deltaTime,
					0,
					true,
				);
			case "right":
				return this.cameraControls.rotate(
					CAMERA_SPEED_DEFAULTS.rotateRight *
						MathUtils.DEG2RAD *
						event?.deltaTime,
					0,
					true,
				);
			default:
				return;
		}
	}

	#handleCameraTruck(
		direction: "up" | "down" | "left" | "right",
		event?: DispatcherEvent,
	) {
		if (!this.isMouseControlEnabled(2)) {
			return;
		}
		switch (direction) {
			case "up":
				return this.cameraControls.truck(
					0,
					CAMERA_SPEED_DEFAULTS.upTruck * event?.deltaTime,
					true,
				);
			case "down":
				return this.cameraControls.truck(
					0,
					-CAMERA_SPEED_DEFAULTS.downTruck * event?.deltaTime,
					true,
				);
			case "left":
				return this.cameraControls.truck(
					CAMERA_SPEED_DEFAULTS.leftTruck * event?.deltaTime,
					0,
					true,
				);
			case "right":
				return this.cameraControls.truck(
					-CAMERA_SPEED_DEFAULTS.rightTruck * event?.deltaTime,
					0,
					true,
				);
		}
	}

	#handleCameraDollyAndZoom(direction: "up" | "down", event?: DispatcherEvent) {
		if (this.isMouseControlEnabled(16)) {
			if (direction === "up") {
				return this.cameraControls.dolly(
					CAMERA_SPEED_DEFAULTS.dollyUp * event?.deltaTime,
					true,
				);
			}
			if (direction === "down") {
				return this.cameraControls.dolly(
					-CAMERA_SPEED_DEFAULTS.dollyDown * event?.deltaTime,
					true,
				);
			}
			return;
		}

		if (this.isMouseControlEnabled(32)) {
			if (direction === "up") {
				return this.cameraControls.zoom(
					CAMERA_SPEED_DEFAULTS.zoomIn * event?.deltaTime,
					true,
				);
			}
			if (direction === "down") {
				return this.cameraControls.zoom(
					-CAMERA_SPEED_DEFAULTS.zoomOut * event?.deltaTime,
					true,
				);
			}
		}
		return;
	}

	#handleArrowKeys(event?: DispatcherEvent) {
		const key = event?.originalEvent?.key;
		const direction = this.keyDirectionMapping[key || ""];

		if (
			event?.originalEvent?.ctrlKey ||
			event?.originalEvent?.metaKey ||
			event?.originalEvent?.altKey
		) {
			return;
		}

		if (event?.originalEvent?.shiftKey) {
			return this.#handleCameraTruck(direction, event);
		}

		return this.#handleCameraRotation(direction, event);
	}

	#handlePlusAndMinusKeys(event?: DispatcherEvent) {
		const key = event?.originalEvent?.key;

		if (key === "+") {
			// '+' key pressed
			return this.#handleCameraDollyAndZoom("up", event);
		}
		if (key === "-") {
			// '-' key pressed
			return this.#handleCameraDollyAndZoom("down", event);
		}

		return;
	}
}
